if (!customElements.get('tiaro-business-inquiry')) {
  class TiaroBusinessInquiry extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.data = JSON.parse(this.querySelector('[data-business-product]').textContent);
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      this.entries = Array.from(this.section.querySelectorAll('[data-business-dialog]'))
        .filter(button => button.dataset.businessDialog === this.dialog.id);
      this.storageKey = `tiaro-business-draft:${this.dataset.productId}`;
      this.entries.forEach(button => button.addEventListener('click', () => this.open(button), { signal }));
      this.querySelectorAll('[data-business-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), { signal }));
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('tiaro-business-open');
        this.entries.forEach(button => button.setAttribute('aria-expanded', 'false'));
        this.cleanReturnUrl();
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, { signal });
      const onVariantChange = event => {
        if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
        this.variant = event.detail.variant || null;
        this.updateProductContext();
      };
      document.addEventListener('product:variant-change', onVariantChange, { signal });
      document.documentElement.addEventListener('product:variant-change', onVariantChange, { signal });
      this.form.addEventListener('submit', () => {
        this.updateReturnUrl();
        this.saveDraft();
      }, { signal });
      this.restoreDraft();
      this.updateProductContext();
      const feedback = this.querySelector('[data-business-success], [data-business-errors]');
      if (feedback) {
        this.open();
        feedback.focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.classList.remove('tiaro-business-open');
      }
    }

    currentVariant() {
      const selector = this.section.querySelector('variant-selects, variant-radios');
      if (selector) {
        const options = Array.from(selector.querySelectorAll('fieldset')).map(fieldset => fieldset.querySelector('select')?.value || fieldset.querySelector('input:checked')?.value);
        const match = this.data.variants.find(variant => options.length === variant.options.length && variant.options.every((option, index) => option === options[index]));
        if (match) return match;
      }
      const urlId = new URL(window.location.href).searchParams.get('variant');
      const fromUrl = this.data.variants.find(variant => String(variant.id) === urlId);
      if (fromUrl) return fromUrl;
      const input = this.section.querySelector('.product-information form[action*="/cart/add"] [name="id"]');
      const selected = this.data.variants.find(variant => String(variant.id) === String(input?.value));
      return selected || this.variant || this.data.variants.find(variant => variant.id === this.data.initialVariant);
    }

    productUrl() {
      const url = new URL(this.data.url, window.location.origin);
      const variant = this.currentVariant();
      if (variant) url.searchParams.set('variant', variant.id);
      return url;
    }

    updateProductContext() {
      const field = this.form.querySelector('[data-business-context]');
      if (!field) return;
      const variant = this.currentVariant();
      const codeCell = Array.from(this.section.querySelectorAll('th')).find(cell => /^(商品番号|商品品番|品番)$/.test(cell.textContent.trim()));
      const code = codeCell?.nextElementSibling?.textContent.trim() || variant?.sku || '';
      const lines = [];
      if (code) lines.push(`${this.data.labels.code}：${code}`);
      if (variant?.sku && variant.sku !== code) lines.push(`${this.data.labels.sku}：${variant.sku}`);
      if (variant?.title && variant.title !== 'Default Title') lines.push(`${this.data.labels.variant}：${variant.title}`);
      lines.push(`${this.data.labels.url}：${this.productUrl().href}`);
      const nextValue = lines.join('\n');
      if (!field.value || field.value === this.lastContext) field.value = nextValue;
      this.lastContext = nextValue;
      this.updateReturnUrl();
    }

    updateReturnUrl() {
      const input = this.form.querySelector('[name="return_to"]');
      if (!input) return;
      const url = this.productUrl();
      url.searchParams.set('contact_posted', 'true');
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      url.hash = this.form.id;
      input.value = `${url.pathname}${url.search}${url.hash}`;
    }

    open(button) {
      this.opener = button || this.entries.find(entry => entry.getClientRects().length);
      this.entries.forEach(entry => entry.setAttribute('aria-expanded', 'true'));
      this.updateProductContext();
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('tiaro-business-open');
      this.dialog.querySelector('h2').focus({ preventScroll: true });
      this.dialog.scrollTop = 0;
    }

    saveDraft() {
      const fields = {};
      this.form.querySelectorAll('input, select, textarea').forEach(input => {
        if (input.name.startsWith('contact[') && input.type !== 'hidden') fields[input.name] = input.value;
      });
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ fields, time: Date.now() })); } catch {}
    }

    restoreDraft() {
      try {
        if (this.querySelector('[data-business-success]')) {
          sessionStorage.removeItem(this.storageKey);
          return;
        }
        const draft = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!draft) return;
        if (Date.now() - draft.time > 1800000) {
          sessionStorage.removeItem(this.storageKey);
          return;
        }
        this.form.querySelectorAll('input, select, textarea').forEach(input => {
          if (Object.hasOwn(draft.fields, input.name)) input.value = draft.fields[input.name];
        });
      } catch {}
    }

    cleanReturnUrl() {
      const url = new URL(window.location.href);
      if (url.hash !== `#${this.form.id}`) return;
      url.searchParams.delete('contact_posted');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.href);
    }
  }
  customElements.define('tiaro-business-inquiry', TiaroBusinessInquiry);
}
